import { useEffect, useRef, useState } from 'react';
import { Group, Progress, Stack, Text } from '@mantine/core';
import { GroceryListGenerationProgress } from '../../types/mealPlan';
import { formatQuantity } from '../../utils/formatQuantity';
import styles from './StreamProgress.module.css';

type Props = {
  /** pending means the task is queued; generating means it is running. */
  status: 'pending' | 'generating';
  progress?: GroceryListGenerationProgress;
};

/**
 * Where the bar sits, by phase.
 *
 * The list is thought through before it is written: with reasoning enabled the
 * first stretch produces no items at all, so reasoning volume advances the bar
 * there, the way the plan generation bar handles its planning stage. Items
 * carry it the rest of the way, capped short of full because the total is an
 * estimate; the finish comes from groceryListStatus flipping, not from here.
 */
const QUEUED_PERCENT = 4;
const THINKING_FLOOR = 6;
const WRITING_FLOOR = 25;
const WRITING_CEILING = 95;

/**
 * Asymptotic like the plan bar's planning stage, and for the same reason: the
 * total reasoning is unknowable ahead of time. Grocery runs reason less than
 * plan generation, so the scale is shorter; measured runs summarize a few
 * thousand characters, and this keeps the median run moving through the
 * middle of its range instead of parking at either end.
 */
const THINKING_SCALE_CHARS = 2500;

const barPercent = (progress?: GroceryListGenerationProgress): number => {
  if (!progress) return QUEUED_PERCENT;

  if (!progress.outputStarted) {
    return (
      THINKING_FLOOR +
      (WRITING_FLOOR - THINKING_FLOOR) *
        (1 - Math.exp(-(progress.thinkingChars ?? 0) / THINKING_SCALE_CHARS))
    );
  }

  const share =
    progress.estimatedTotalItems > 0
      ? Math.min(progress.itemsSoFar / progress.estimatedTotalItems, 1)
      : 0;
  return WRITING_FLOOR + share * (WRITING_CEILING - WRITING_FLOOR);
};

const statusLabel = (
  status: 'pending' | 'generating',
  progress?: GroceryListGenerationProgress
): string => {
  // A restart is worth saying out loud, otherwise the count emptying reads as
  // progress being lost.
  if ((progress?.attempt ?? 1) > 1 && (progress?.itemsSoFar ?? 0) === 0) {
    return 'Giving your list another go';
  }

  // Written from the kitchen's side: what a person building your shopping
  // list would say they were doing. Nothing narrates how it is produced.
  if (status === 'pending' || !progress) return 'Preparing your list';
  if (!progress.outputStarted || progress.itemsSoFar === 0) {
    return 'Going over your recipes';
  }
  return 'Building your list';
};

/**
 * The count reads against the estimate only while it is below it. The
 * estimate is a median, so a plan can legitimately pass it, and "45 of about
 * 42" reads as the list being wrong rather than as it being nearly done.
 */
const countLabel = (progress?: GroceryListGenerationProgress): string | null => {
  if (!progress || progress.itemsSoFar === 0) return null;
  if (progress.itemsSoFar < progress.estimatedTotalItems) {
    return `${progress.itemsSoFar} of about ${progress.estimatedTotalItems} items`;
  }
  return `${progress.itemsSoFar} items`;
};

type FeaturedItem = NonNullable<GroceryListGenerationProgress['latestItem']>;

const itemKey = (item: FeaturedItem): string =>
  `${item.name}|${item.quantity}|${item.unit}`;

/** How long each featured item holds before the next may replace it. */
const FEATURE_HOLD_MS = 1000;

/**
 * Paces the featured line to a readable cadence.
 *
 * Items complete every few hundred milliseconds, faster than anyone can read.
 * The server publishes every one honestly; this hook holds each shown item
 * for FEATURE_HOLD_MS and then swaps to whatever the newest item is, silently
 * skipping the ones that landed in between. The line is ambient texture, not
 * a log, so skipping is correct; the count above it stays exact.
 */
export const usePacedFeaturedItem = (
  item: FeaturedItem | undefined
): FeaturedItem | undefined => {
  const [shown, setShown] = useState<FeaturedItem | undefined>(item);
  const shownAtRef = useRef<number>(item ? Date.now() : 0);

  const incomingKey = item ? itemKey(item) : undefined;
  const shownKey = shown ? itemKey(shown) : undefined;

  useEffect(() => {
    // A retry restart clears the item; stale names must not linger over an
    // empty run.
    if (!item) {
      setShown(undefined);
      shownAtRef.current = 0;
      return;
    }
    if (incomingKey === shownKey) return;

    const swap = () => {
      setShown(item);
      shownAtRef.current = Date.now();
    };

    const elapsed = Date.now() - shownAtRef.current;
    if (elapsed >= FEATURE_HOLD_MS) {
      swap();
      return;
    }
    // A newer item arriving before this fires re-runs the effect, cancels
    // this timer, and schedules its own swap: latest wins at the deadline.
    const id = setTimeout(swap, FEATURE_HOLD_MS - elapsed);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingKey]);

  return shown;
};

/**
 * Shows the shopping list taking shape while it is still being written.
 *
 * Fills the status slot the plain "Building your list..." spinner used to
 * occupy: a bar that moves through the reasoning stretch, an honest count
 * against an estimated total, and the item that just landed.
 */
export const GroceryListProgress = ({ status, progress }: Props) => {
  const featured = usePacedFeaturedItem(progress?.latestItem);
  const count = countLabel(progress);

  return (
    <Stack gap={6}>
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <Text size="xs" fw={500} lh={1.3}>
          {statusLabel(status, progress)}
        </Text>
        {count && (
          <Text size="xs" c="dimmed" lh={1.3} style={{ whiteSpace: 'nowrap' }}>
            {count}
          </Text>
        )}
      </Group>
      <Progress
        value={barPercent(progress)}
        size="xs"
        radius="xl"
        color="blue"
        classNames={{ section: styles.barFill }}
        transitionDuration={400}
      />
      <Text
        key={featured ? itemKey(featured) : 'empty'}
        size="xs"
        c="dimmed"
        lh={1.3}
        lineClamp={1}
        className={`${styles.featuredItem} ${styles.featuredLine}`}
      >
        {featured ? (
          <>
            Just added{' '}
            <Text component="span" fw={600} inherit>
              {formatQuantity(featured.quantity, featured.unit)} {featured.name}
            </Text>
          </>
        ) : (
          ''
        )}
      </Text>
    </Stack>
  );
};
